import {
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemProps,
    ListItemText,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button
} from "@mui/material";

import { Attachment } from "../../models";
import { Storage } from "@aws-amplify/storage";
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { format } from "date-fns";
import { useCallback } from "react";
import { useState } from "react";

type RecordingListItemProps = ListItemProps & {
    attachment: Attachment;
    onRename?: (attachment: Attachment, newName: string) => void;
};

export const RecordingListItem = ({ attachment, onRename, ...props }: RecordingListItemProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [attachmentNewName, setAttachmentNewName] = useState("");
    const [currentAttachmentName, setCurrentAttachmentName] = useState(attachment.name || format(new Date(attachment.createdAt!), "MMMM do, yyyy h:mm a"));

    const onClick = useCallback(async () => {
        const presignedUrl = await Storage.get(attachment.key!);
        window.open(presignedUrl, "_blank");
    }, [attachment]);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const openEditNameModal = async () => {
        handleMenuClose();
        setAttachmentNewName(currentAttachmentName);
        setRenameDialogOpen(true);
    };

    const popupCancel = () => {
        setRenameDialogOpen(false);
        setAttachmentNewName("");
    };

    const handleSaveRename = useCallback(async () => {
        setCurrentAttachmentName(attachmentNewName.trim());
        if (onRename) {
            onRename(attachment, attachmentNewName.trim());
        }
        setRenameDialogOpen(false);
        setAttachmentNewName("");
    }, [attachmentNewName, currentAttachmentName, attachment, onRename]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAttachmentNewName(event.target.value);
    };

    return (
        <>
            <ListItem {...props} secondaryAction={
                <>
                    <IconButton
                        edge="end"
                        aria-label="more options"
                        onClick={handleMenuClick}
                    >
                        <MoreVertIcon />    
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button',
                        }}
                    >
                        <MenuItem onClick={openEditNameModal}>Rename</MenuItem>
                    </Menu>
                </>
            }>
                <ListItemButton onClick={onClick}>
                    <ListItemIcon>
                        <VideocamOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText>
                        {currentAttachmentName}
                    </ListItemText>
                </ListItemButton>
            </ListItem>

            <Dialog
                open={renameDialogOpen}
                onClose={popupCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Rename File</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Recording Name"
                        fullWidth
                        variant="outlined"
                        value={attachmentNewName}
                        onChange={handleNameChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={popupCancel}>Cancel</Button>
                    <Button 
                        onClick={handleSaveRename} 
                        variant="contained"
                        disabled={!attachmentNewName.trim() || attachmentNewName.trim() === currentAttachmentName}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};